'use client';

import {
	Alert,
	Checkbox,
	CheckboxGroup,
	Combobox,
	createTheme,
	Input,
	InputPlaceholder,
	InputWrapper,
	MantineProvider,
	type MantineThemeColorsOverride,
	type MantineThemeOverride,
	RadioGroup,
	SegmentedControl,
	Select,
	Tabs,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { ModalsProvider } from '@mantine/modals';
import type { PropsWithChildren } from 'react';

// Mantine styles (note core must come first)
import '@mantine/core/styles.layer.css';
import '@mantine/dates/styles.layer.css';

// Overrides
import alertStyles from './MantineAlert.module.scss';
import checkboxStyles from './MantineCheckbox.module.scss';
import checkboxGroupStyles from './MantineCheckboxGroup.module.scss';
import comboboxStyles from './MantineCombobox.module.scss';
import inputWrapperStyles from './MantineInputWrapper.module.scss';
import radioGroupStyles from './MantineRadioGroup.module.scss';
import segmentedControlStyles from './MantineSegmentedControl.module.scss';
import tabsStyles from './MantineTabs.module.scss';
import styles from './MantineWrapper.module.scss';

const themeColors: MantineThemeColorsOverride = {
	brandBlue: [
		'#eff2fb',
		'#dbe1f0',
		'#b3bfe3',
		'#899cd7',
		'#667ecc',
		'#506cc6',
		'#4462c5',
		'#3752ae',
		'#2f499c',
		'#1e3575',
	],
	blue: [
		'#e6f7ff',
		'#d0eaff',
		'#9fd2fc',
		'#6bbafb',
		'#44a5fa',
		'#2f98fa',
		'#2391fb',
		'#157ee0',
		'#0072ce',
		'#0060b2',
	],
	primitivePurple: [
		'#f4f1f9',
		'#e5deed',
		'#cabadc',
		'#ad93cb',
		'#9572bd',
		'#875eb4',
		'#7f53b1',
		'#6d449c',
		'#613c8b',
		'#523178',
	],
	primitiveGreen: [
		'#f4faf0',
		'#e8f2e0',
		'#cde4bb',
		'#b0d694',
		'#98ca72',
		'#88c25d',
		'#80bf51',
		'#6da842',
		'#609539',
		'#4a7729',
	],
	primitiveAqua: [
		'#ebfcff',
		'#d7f7fc',
		'#aaeffa',
		'#7ce6fa',
		'#60dff8',
		'#53dbf8',
		'#4bd9f9',
		'#3ec0de',
		'#2eabc6',
		'#007c91',
	],
};
const themeOverrides: MantineThemeOverride = {
	colors: themeColors,
	fontFamily: 'Open Sans, Verdana, sans-serif',
	fontSizes: {
		xs: '11',
		sm: '12',
		md: '16',
		lg: '18',
		xl: '20',
	},
	primaryColor: 'blue',
	components: {
		Alert: Alert.extend({
			classNames: alertStyles,
		}),
		Checkbox: Checkbox.extend({
			classNames: checkboxStyles,
		}),
		CheckboxGroup: CheckboxGroup.extend({
			classNames: checkboxGroupStyles,
		}),
		Combobox: Combobox.extend({
			classNames: comboboxStyles,
		}),
		DatePickerInput: DatePickerInput.extend({
			classNames: styles,
		}),
		Input: Input.extend({
			classNames: styles,
		}),
		InputWrapper: InputWrapper.extend({
			classNames: inputWrapperStyles,
		}),
		InputPlaceholder: InputPlaceholder.extend({
			classNames: styles,
		}),
		RadioGroup: RadioGroup.extend({
			classNames: radioGroupStyles,
		}),
		SegmentedControl: SegmentedControl.extend({
			classNames: segmentedControlStyles,
		}),
		Select: Select.extend({
			classNames: comboboxStyles,
		}),
		Tabs: Tabs.extend({
			classNames: tabsStyles,
		}),
	},
};

const theme = createTheme(themeOverrides);

/**
 * Setup styles for Mantine components.
 *
 * @param child nodes to be rendered inside the MantineProvider.
 * @returns
 */
export function MantineWrapper({ children }: PropsWithChildren) {
	return (
		<MantineProvider theme={theme}>
			<ModalsProvider>{children}</ModalsProvider>
		</MantineProvider>
	);
}
